import { useCallback, useMemo, useState } from 'react';
import _ from 'lodash';
import Fuse from 'fuse.js';

import useProductQueryParams from 'hooks/use-product-query-params';
import { Terpenes } from './terpenes-filter.types';

const TERPENES_DISPLAY_THRESHOLD = 6;

type ParsedTerpene = {
  key: string;
  label: string;
  value: string;
};

type ParsedTerpenes = ParsedTerpene[];

type TerpenesFilterReturn = {
  terpenesFromQueryParams: string[];
  terpeneOptionsToRender: () => ParsedTerpene[];
  defaults: any;
  handleTerpeneSelection: (terpene: string) => void;
  setSearch: (search: string) => void;
  search: string;
  setExpanded: (expanded: boolean) => void;
  expanded: boolean;
  displaySearchBar: boolean;
  displayViewMore: boolean;
  filteredTerpenes: ParsedTerpenes;
};

type Option = {
  key: string;
  value: string;
  label: string;
  queryValue?: string;
};

export function useTerpenesFilter(terpenes: Terpenes): TerpenesFilterReturn {
  const { defaults, queryParams, setQueryParams } = useProductQueryParams();
  const [search, setSearch] = useState('');

  const consolidatedTerpenes = _.uniqBy(terpenes, `name`);
  const parsedTerpenes = consolidatedTerpenes.map(({ id, name }) => ({
    key: id,
    value: _.kebabCase(name),
    label: name,
  }));

  // Create Fuse instance with options
  const fuseInstance = useMemo(() => {
    const options = {
      keys: ['value', 'label'], // Search both the kebab-case value and original name
      threshold: 0.3, // Lower threshold = more strict matching (0.0 - 1.0)
      ignoreLocation: true,
    };
    return new Fuse(parsedTerpenes, options);
  }, [parsedTerpenes]);

  const filteredTerpenes = useMemo(() => {
    if (!search) {
      return parsedTerpenes;
    }
    return fuseInstance.search(search).map((result) => result.item);
  }, [parsedTerpenes, search, fuseInstance]);

  const terpenesFromQueryParams = useMemo(() => queryParams.terpenes || [], [queryParams.terpenes]);

  const setupViewMoreExpanded = (options: Option[], selectedOptions: string[] | null): boolean => {
    const optionsPastSix = options.slice(TERPENES_DISPLAY_THRESHOLD);
    return optionsPastSix.some((option) => selectedOptions?.includes(option.queryValue ?? option.value));
  };

  const initiallyExpanded = useMemo(() => setupViewMoreExpanded(filteredTerpenes, terpenesFromQueryParams), [
    filteredTerpenes,
    terpenesFromQueryParams,
  ]);
  const [expanded, setExpanded] = useState(initiallyExpanded);

  const handleTerpeneSelection = useCallback(
    (terpene: string) => {
      // If terpenesInQuery contains terpene, remove it
      // If terpenesInQuery doesn't contain terpene, add it
      const newTerpenes = _.xor(terpenesFromQueryParams, [terpene]);

      setQueryParams({
        terpenes: newTerpenes,
        page: defaults.page,
      });
      setExpanded(false);
    },
    [terpenesFromQueryParams, defaults.page, setQueryParams]
  );

  const terpeneOptionsToRender = (): ParsedTerpene[] => {
    const selectedTerpenes = parsedTerpenes.filter((terpene) => terpenesFromQueryParams?.includes(terpene.value));

    const finalList = _.uniqBy([...selectedTerpenes, ...filteredTerpenes], 'value');

    // Sort to place selected terpenes at the top (alphabetically)
    finalList.sort((a, b) => {
      const aSelected = selectedTerpenes.some((terpene) => terpene.value === a.value);
      const bSelected = selectedTerpenes.some((terpene) => terpene.value === b.value);

      // If both are selected, sort alphabetically by label
      if (aSelected && bSelected) {
        return a.label.localeCompare(b.label);
      }

      // If only one is selected, it comes first
      if (aSelected) {
        return -1;
      }
      if (bSelected) {
        return 1;
      }

      return 0;
    });

    return finalList;
  };

  const displaySearchBar = parsedTerpenes.length > TERPENES_DISPLAY_THRESHOLD;
  const displayViewMore = filteredTerpenes.length > TERPENES_DISPLAY_THRESHOLD;

  return {
    terpenesFromQueryParams,
    terpeneOptionsToRender,
    defaults,
    handleTerpeneSelection,
    setSearch,
    search,
    setExpanded,
    expanded,
    displaySearchBar,
    displayViewMore,
    filteredTerpenes,
  };
}
